import { modelName } from '@/lib/npc-replays-labels';
import { costLabel, sessionTime } from '@/lib/session-labels';
import type {
  ReplayFilter,
  ReplayMarkNpc,
  ReplayMarkSeries,
  ReplayMode,
  ReplayOrigin,
  ReplayPlayer,
  ReplaySourceKind,
  SessionStatus,
} from '@matter_extractor/shared';

/**
 * What the screens of Sessions say of a REPLAY (rejeu.md section 12, issue
 * #193) - pure, in the voice of the maquettes `Liste` and `Parcours`: the pill,
 * the sentence of each mode, the words for a source, the tenth filter.
 *
 * A file of its own, like `review-entry.ts` for the review: the list, the
 * parcours and the conversation all need the same four or five words, and a
 * label spelt in two places is a label that drifts (« complété » on the list,
 * « completé » on the head).
 *
 * The enumerations (`ReplayMode`, `ReplaySourceKind`, `ReplayFilter`) are the
 * contract's, in `shared`: this file words them and decides nothing.
 */

/* ------------------------------------------------------------------ the pill */

/** The mode as the pill and the panel say it: « strict », « complété ». */
export const REPLAY_MODE_LABEL: Readonly<Record<ReplayMode, string>> = {
  strict: 'strict',
  completed: 'complété',
};

/** « rejeu · strict », « rejeu · complété » - the pill of the list, the parcours and the conversation. */
export function replayPillLabel(mode: ReplayMode): string {
  return `rejeu · ${REPLAY_MODE_LABEL[mode]}`;
}

/** The sentence under the mode in the panel (§12): what the player was allowed. */
export const REPLAY_MODE_SENTENCE: Readonly<Record<ReplayMode, string>> = {
  strict: 'rien hors de la source',
  completed: 'complété dans l’esprit de la source',
};

/* ---------------------------------------------------------------- the source */

/** What a source is, with its article: « un écrit », « une session », « un entretien ». */
export const REPLAY_SOURCE_KIND_LABEL: Readonly<Record<ReplaySourceKind, string>> = {
  written: 'un écrit',
  session: 'une session',
  interview: 'un entretien',
};

/** `2 400 caractères`, grouped with the thin space France groups thousands with; `1 caractère` in the singular. */
export function replaySourceSize(chars: number): string {
  const grouped = String(Math.round(chars)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped} ${chars > 1 ? 'caractères' : 'caractère'}`;
}

const MONTHS_SHORT = [
  'janv.',
  'févr.',
  'mars',
  'avril',
  'mai',
  'juin',
  'juill.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
] as const;

/** `14 oct. 2026 à 15:02`, written by hand: `Intl` answers by the locale of the MACHINE. */
export function replayStartedLabel(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const month = MONTHS_SHORT[date.getMonth()] ?? '';
  return `${date.getDate()} ${month} ${date.getFullYear()} à ${sessionTime(iso)}`;
}

/* ------------------------------------------------------------- the origin */

/**
 * The fact « Rejoue » of the panel: the replay this one replayed.
 *
 * Three shapes. No origin: « aucun ». An origin still readable (its experience
 * version is known): a link, and « même source, sur la v20 ». An origin gone
 * (purged with its session, wiped by a refresh of the dev model, so its version
 * is null): the words and no link, because the link would lead to « Aucune
 * session sous cet identifiant ».
 */
export interface ReplayOriginFact {
  readonly kind: 'none' | 'linked' | 'gone';
  /** The words of the fact: « le rejeu 7c1e4a90 », « un rejeu effacé », « aucun ». */
  readonly label: string;
  /** « même source, sur la v20 », or « même source » when the version is not known; null on none. */
  readonly detail: string | null;
  readonly replayId: string | null;
}

/** The 8 first characters of an identifier: how an operator names a replay aloud. */
export function shortReplayId(id: string): string {
  return id.slice(0, 8);
}

export function replayOriginFact(origin: ReplayOrigin | null): ReplayOriginFact {
  if (origin === null) return { kind: 'none', label: 'aucun', detail: null, replayId: null };
  if (origin.experienceVersionNumber === null) {
    return {
      kind: 'gone',
      label: 'un rejeu effacé',
      detail: 'même source',
      replayId: origin.replayId,
    };
  }
  return {
    kind: 'linked',
    label: `le rejeu ${shortReplayId(origin.replayId)}`,
    detail: `même source, sur la v${origin.experienceVersionNumber}`,
    replayId: origin.replayId,
  };
}

/* ------------------------------------------------------------- the head */

/**
 * The subtitle of a replay's head (§12): « rejouée par Claude, sans lien
 * d'entrée » where an ordinary session says « entrée par le lien ... ». The
 * bold part is `REPLAY_HEAD_LEAD`, the rest `REPLAY_HEAD_TAIL`.
 */
export const REPLAY_HEAD_LEAD = 'rejouée par Claude';
export const REPLAY_HEAD_TAIL = ', sans lien d’entrée';

/**
 * The bold part of that subtitle for the player (npc.md §13.2): « rejouée par le serveur » where
 * the server played the replay, `REPLAY_HEAD_LEAD` for Claude - and for a mark that predates the
 * NPC, which carries no player and reads as Claude's.
 */
export function replayHeadLead(player: ReplayPlayer | undefined): string {
  return player === 'server' ? 'rejouée par le serveur' : REPLAY_HEAD_LEAD;
}

/* ------------------------------------------------------------ the filter */

/** The chip of the tenth filter once armed: « Rejeu : avec ». */
export function replayFilterChipLabel(filter: ReplayFilter): string {
  return `Rejeu : ${filter === 'with' ? 'avec' : 'sans'}`;
}

/** What the selector offers, in the voice of the maquette `Liste`. */
export const REPLAY_FILTER_OPTION_LABEL: Readonly<Record<ReplayFilter, string>> = {
  with: 'Les rejeux seulement',
  without: 'Sans les rejeux',
};

/** The sentence under the panel of « Plus de filtres » (§12, npc.md §13.1): both players. */
export const REPLAY_FILTER_HELP =
  '« Rejeu » retient les sessions jouées à la place d’une personne, par Claude ou par le serveur.';

/** The chip of the eleventh filter once armed: « NPC : Marie Curie ». */
export function npcFilterChipLabel(name: string): string {
  return `NPC : ${name}`;
}

/** The sentence under the panel for the NPC filter (npc.md §13.1). */
export const NPC_FILTER_HELP =
  '« NPC » retient les rejeux d’un NPC, toutes versions, et ceux rattachés après coup.';

/** What the address bar and the server spell. */
export function isReplayFilter(value: unknown): value is ReplayFilter {
  return value === 'with' || value === 'without';
}

/* ------------------------------------------------------------ the sentence */

/** The sentence « Pour rejouer ce cas ... » of the panel, up to the identifier. */
export const REPLAY_AGAIN_LEAD =
  'Pour rejouer ce cas sur la version suivante, dire à Claude : « rejoue ce rejeu », avec l’identifiant';

/* ------------------------------------------------------- the NPC of a replay */

/** What a replay attached afterwards says of itself (npc.md §13.1, N17): it was played before the NPC. */
export const REPLAY_BEFORE_NPC_LABEL = 'joué avant les NPC';

/**
 * The words a SEEDED series carries after its date, in brackets, as the server composes the
 * label (`seriesLabelOf`): « Référence v24, 23 oct. (jouée avant les NPC) ». The list says it
 * once, on the line of the NPC, and keeps the series line clean.
 */
const SEEDED_SERIES_SUFFIX = /\s*\(jouée avant les NPC\)\s*$/;

/** « Marie Curie · v2 »: the NPC and the version the replay froze. */
export function replayNpcLabel(npc: Pick<ReplayMarkNpc, 'name' | 'versionNumber'>): string {
  return `${npc.name} · v${npc.versionNumber}`;
}

/**
 * The line under the NPC (npc.md §13.1): the series, « Petit banc #204, 27 oct. », cleared of the
 * words of a seeded series, and whether it was one. Null when the replay belongs to no series.
 */
export function replaySeriesOf(
  series: Pick<ReplayMarkSeries, 'label'> | null | undefined,
): { readonly text: string; readonly seeded: boolean } | null {
  if (series === null || series === undefined) return null;
  const seeded = SEEDED_SERIES_SUFFIX.test(series.label);
  return { text: series.label.replace(SEEDED_SERIES_SUFFIX, ''), seeded };
}

/* ------------------------------------------------------ the panel of a replay */

/** The title of the panel (npc.md §13.2): the server played it, or Claude did. */
export function replayPanelTitle(player: ReplayPlayer | undefined): string {
  return player === 'server' ? 'Rejeu joué par le serveur' : 'Rejeu par Claude';
}

/**
 * What the line under the title says of where the replay came from and who played it (npc.md
 * §13.2): « depuis l'app · joué par Claude Sonnet 5.5 » for the server, whose model is the real
 * one; « depuis l'assistant MCP · Claude Opus 5.5 (déclaré) » for Claude, whose model is what he
 * declared - and nothing of a model he did not declare.
 */
export interface ReplayLaunchLine {
  readonly from: string;
  /** « joué par » before the model, only where the server played. */
  readonly playedBy: boolean;
  readonly model: string | null;
  readonly declared: boolean;
}

export function replayLaunchLine(replay: {
  readonly player?: ReplayPlayer | undefined;
  readonly playerModel: string | null;
}): ReplayLaunchLine {
  if (replay.player === 'server') {
    return {
      from: 'depuis l’app',
      playedBy: true,
      model: replay.playerModel === null ? null : modelName(replay.playerModel),
      declared: false,
    };
  }
  return {
    from: 'depuis l’assistant MCP',
    playedBy: false,
    model: replay.playerModel,
    declared: true,
  };
}

/** « joueur : 0,18 € · le dispositif : 1,08 € » (H15): the two costs, told apart. */
export function replayCostsSentence(playerCostEur: number, deviceCostEur: number): string {
  return `joueur : ${costLabel(playerCostEur)} · le dispositif : ${costLabel(deviceCostEur)}`;
}

/**
 * The issue of a replay, as the panel reads it off the status of its session (npc.md §13.2): the
 * word and the line under it. The series records a finer outcome (« départ », « bloquée »), which
 * the detail of a session does not carry - the panel says what the session itself says.
 */
export function replayIssueOf(status: SessionStatus): { word: string; detail: string } {
  switch (status) {
    case 'finished':
      return { word: 'finie', detail: 'la dernière page atteinte' };
    case 'in-progress':
      return { word: 'en cours', detail: 'le rejeu se joue encore' };
    case 'interrupted':
      return { word: 'interrompue', detail: 'arrêtée avant la dernière page' };
    case 'closed':
      return { word: 'fermée', detail: 'close par le dispositif avant la dernière page' };
    case 'expired':
      return { word: 'expirée', detail: 'la personne est partie, la session a expiré' };
  }
}

/** « passe 2 » under the series, or null outside one. */
export function replayPassLine(pass: number | null | undefined): string | null {
  return pass === null || pass === undefined ? null : `passe ${pass}`;
}

/** « figées à la v2 · 6 095 caractères » under « Les sources du NPC ». */
export function npcSourcesLine(versionNumber: number, chars: number): string {
  return `figées à la v${versionNumber} · ${replaySourceSize(chars)}`;
}

/** The sentence that replaces `REPLAY_AGAIN_LEAD` on a replay of an NPC (maquette `SessionsReglages`). */
export const NPC_AGAIN_LEAD = 'Pour rejouer ce cas sur la version suivante : relancer depuis';
export const NPC_AGAIN_TAIL = 'ou dire à Claude « rejoue ce rejeu », avec l’identifiant';
